import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RouteNames } from "../../../navigation/RouteNames";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  clearAdminAuthError,
  clearRecoveryCodes,
  completeAdminTwoFactorChallenge,
  confirmAdminTwoFactorSetup,
  fetchAdminTwoFactorSetup,
  finalizeTwoFactorSetupAfterSavingRecoveryCodes,
  loginAdmin,
} from "../../../store/slices/adminAuthSlice";
import { saveToken } from "../../../utils/helperFunctions/tokenStorage";

export const useAdminLoginScreen = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    loading,
    error,
    fieldErrors,
    token,
    twoFactorMode,
    setupToken,
    challengeToken,
    setupSecret,
    setupOtpAuthUri,
    recoveryCodes,
    pendingToken,
  } = useAppSelector((state) => state.adminAuth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    if (token && recoveryCodes.length === 0) {
      navigate(RouteNames.accounts, { replace: true });
    }
  }, [recoveryCodes.length, token, navigate]);

  useEffect(() => {
    if (twoFactorMode === "setup" && setupToken && !setupSecret) {
      dispatch(fetchAdminTwoFactorSetup(setupToken));
    }
  }, [dispatch, setupSecret, setupToken, twoFactorMode]);

  useEffect(() => {
    return () => {
      dispatch(clearAdminAuthError());
      dispatch(clearRecoveryCodes());
    };
  }, [dispatch]);

  const handleSubmit = async () => {
    if (twoFactorMode === "setup") {
      if (!setupToken) {
        return;
      }

      await dispatch(
        confirmAdminTwoFactorSetup({
          setupToken,
          body: { code },
        }),
      );

      return;
    }

    if (twoFactorMode === "challenge") {
      if (!challengeToken) {
        return;
      }

      const result = await dispatch(
        completeAdminTwoFactorChallenge({
          challengeToken,
          body: { code },
        }),
      );

      if (completeAdminTwoFactorChallenge.fulfilled.match(result)) {
        navigate(RouteNames.accounts, { replace: true });
      }

      return;
    }

    await dispatch(
      loginAdmin({
        email,
        password,
      }),
    );
  };

  const handleContinueAfterSavingRecoveryCodes = async () => {
    if (!pendingToken) {
      return;
    }

    await saveToken(pendingToken);
    dispatch(finalizeTwoFactorSetupAfterSavingRecoveryCodes());
    navigate(RouteNames.accounts, { replace: true });
  };

  const getFieldError = (key: string) => fieldErrors[key]?.[0];

  return {
    email,
    setEmail,
    password,
    setPassword,
    code,
    setCode,
    loading,
    error,
    getFieldError,
    handleSubmit,
    twoFactorMode,
    setupSecret,
    setupOtpAuthUri,
    recoveryCodes,
    handleContinueAfterSavingRecoveryCodes,
  };
};
